import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Smartphone, Tablet, Laptop, Gamepad2, HelpCircle, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr, SHOP } from "@/lib/api";
import AuthForm from "@/pages/AuthForm";
import Seo from "@/components/Seo";

const DEVICE_TYPES = [
  { id: "phone", label: "Phone", icon: Smartphone },
  { id: "tablet", label: "Tablet", icon: Tablet },
  { id: "laptop", label: "Laptop", icon: Laptop },
  { id: "console", label: "Games console", icon: Gamepad2 },
  { id: "other", label: "Something else", icon: HelpCircle },
];

const ISSUES = ["Cracked / broken screen", "Battery draining or not charging", "Charging port fault", "Water damage", "Won't turn on", "Software / performance", "Something else"];

const SLOTS = ["10:30", "11:30", "12:30", "13:30", "14:30", "15:30", "16:30", "17:30"];

const STEPS = ["Device", "Problem", "Delivery", "Review"];

export default function Book() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    device_type: "", brand: "", model: "", issue: "", description: "",
    method: "dropoff", preferred_date: "", preferred_time: "", phone: "", notes: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [prices, setPrices] = useState(null);
  useEffect(() => {
    api.get("/prices").then((r) => setPrices(r.data)).catch(() => setPrices([]));
  }, []);

  const [availability, setAvailability] = useState(null);
  useEffect(() => {
    if (form.method === "dropoff" && form.preferred_date) {
      api.get("/bookings/availability", { params: { date: form.preferred_date } })
        .then((r) => {
          setAvailability(r.data);
          if (r.data.slots[form.preferred_time] >= 3) set("preferred_time", "");
        })
        .catch(() => setAvailability(null));
    } else {
      setAvailability(null);
    }
  }, [form.preferred_date, form.method]);

  const canNext =
    (step === 0 && form.device_type && form.brand.trim() && form.model.trim()) ||
    (step === 1 && form.issue) ||
    (step === 2 && form.phone.trim().length >= 7) ||
    step === 3;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", form);
      setDone(data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const payDeposit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: done._id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(fmtErr(e));
      setBusy(false);
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-24 font-display uppercase tracking-widest" data-testid="book-loading">Loading…</div>;

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-testid="book-auth-gate">
        <div>
          <p className="eyebrow text-accent mb-4">Repair booking</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl leading-[0.95] mb-6">Create an account to book your repair</h1>
          <p className="text-neutral-600 leading-relaxed mb-6 max-w-md">
            Your account keeps everything in one place: booking reference, repair status and warranty record. It takes under a minute.
          </p>
          <ul className="space-y-3 text-sm text-neutral-600">
            <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-accent shrink-0" /> Track your repair from received to ready</li>
            <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-accent shrink-0" /> Bring it to us in Hyde or post your device in</li>
            <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-accent shrink-0" /> Warranty recorded against every job</li>
          </ul>
        </div>
        <AuthForm redirectTo={location.pathname} />
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="booking-success">
        <CheckCircle2 className="h-16 w-16 mx-auto mb-6 text-accent" />
        <h1 className="font-display font-black uppercase tracking-tight text-4xl mb-4">Booking received</h1>
        <p className="text-neutral-600 mb-2">Your booking reference is</p>
        <p className="font-display font-black text-3xl tracking-widest border-2 border-black inline-block px-6 py-3 shadow-hard mb-8" data-testid="booking-reference">{done.reference}</p>
        <p className="text-neutral-600 leading-relaxed max-w-md mx-auto mb-8">
          {done.method === "dropoff"
            ? `Repairs are by booking only — you're booked in. Bring your device to ${SHOP.address} during opening hours (Mon–Fri, 10:30am–6:00pm).`
            : `Package your device securely and post it to Appleman Tech Repair, ${SHOP.address}. Include your booking reference in the parcel. Once fixed, it comes back with free next-day return delivery — guaranteed.`}
        </p>
        <div className="border-2 border-black bg-neutral-50 p-6 mb-8 max-w-md mx-auto text-left" data-testid="deposit-box">
          <p className="font-display font-bold uppercase tracking-wider mb-1">Secure your repair — £10 deposit</p>
          <p className="text-sm text-neutral-600 mb-4">Reserve your slot and parts now; the deposit comes off your final bill. Prefer not to? You can pay in full when we see the device.</p>
          <button onClick={payDeposit} disabled={busy} data-testid="pay-deposit-btn"
            className="w-full border-2 border-black bg-accent px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover disabled:opacity-50">
            {busy ? "Redirecting to Stripe…" : "Pay £10 deposit"}
          </button>
        </div>
        <a href="/dashboard" data-testid="booking-dashboard-link" className="inline-flex items-center gap-2 border-2 border-black bg-black text-white px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover">
          View in my account <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16" data-testid="book-page">
      <Seo title="Book a Repair | Appleman Tech Repair" description="Book your phone, tablet, laptop or console repair online. Repairs by booking only — drop off in Hyde, Tameside or post your device in from anywhere in the UK." />
      <p className="eyebrow text-accent mb-4">Book a repair</p>
      <h1 className="font-display font-black uppercase tracking-tighter text-5xl mb-10">Let's get it fixed</h1>

      <div className="flex gap-2 mb-8" data-testid="booking-steps">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex-1 border-2 border-black px-3 py-2 font-display text-xs font-bold uppercase tracking-wider text-center ${i === step ? "bg-black text-white" : i < step ? "bg-accent" : "bg-white"}`}>
            {s}
          </div>
        ))}
      </div>

      <div className="border-2 border-black bg-neutral-50 p-5 mb-12" data-testid="booking-prices">
        <p className="eyebrow text-neutral-500 mb-3">Typical prices</p>
        {prices && prices.length > 0 ? (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
            {prices.map((p) => (
              <li key={p._id} className="flex justify-between gap-4 border-b border-neutral-200 py-1">
                <span>{p.repair} <span className="text-neutral-400">({p.device_type})</span></span>
                <span className="font-display font-bold whitespace-nowrap">{p.price}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-neutral-600">Every repair is quoted fairly before any work starts — you'll always know the price first.</p>
        )}
      </div>

      {step === 0 && (
        <div data-testid="booking-step-device">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-6">What's the device?</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
            {DEVICE_TYPES.map((d) => (
              <button key={d.id} onClick={() => set("device_type", d.id)} data-testid={`device-type-${d.id}`}
                className={`border-2 border-black p-5 flex flex-col items-center gap-2 font-display font-bold uppercase text-sm tracking-wider shadow-hard-sm brutal-hover ${form.device_type === d.id ? "bg-accent" : "bg-white"}`}>
                <d.icon className="h-7 w-7" /> {d.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2" htmlFor="brand">Brand</label>
              <input id="brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="e.g. Apple, Samsung, Sony" data-testid="booking-brand-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="model">Model</label>
              <input id="model" value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="e.g. iPhone 13, Galaxy S22" data-testid="booking-model-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
          </div>
          <p className="mt-4 text-sm text-neutral-500">Not sure of the model? Tap the green “Identify my device” button — our AI assistant will help.</p>
        </div>
      )}

      {step === 1 && (
        <div data-testid="booking-step-issue">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-6">What's wrong with it?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {ISSUES.map((i) => (
              <button key={i} onClick={() => set("issue", i)} data-testid={`issue-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`border-2 border-black px-4 py-3 text-left font-medium shadow-hard-sm brutal-hover ${form.issue === i ? "bg-accent" : "bg-white"}`}>
                {i}
              </button>
            ))}
          </div>
          <label className="eyebrow block mb-2" htmlFor="description">Anything else we should know?</label>
          <textarea id="description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} data-testid="booking-description-input"
            placeholder="e.g. Screen cracked after a drop, touch still works…"
            className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
        </div>
      )}

      {step === 2 && (
        <div data-testid="booking-step-delivery">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-6">How will it get to us?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <button onClick={() => set("method", "dropoff")} data-testid="method-dropoff"
              className={`border-2 border-black p-6 text-left shadow-hard-sm brutal-hover ${form.method === "dropoff" ? "bg-accent" : "bg-white"}`}>
              <h3 className="font-display font-bold uppercase tracking-wider mb-2">Bring it to us</h3>
              <p className="text-sm text-neutral-600">{SHOP.address}. Mon–Fri 10:30am–6:00pm. Bookings only.</p>
            </button>
            <button onClick={() => set("method", "postin")} data-testid="method-postin"
              className={`border-2 border-black p-6 text-left shadow-hard-sm brutal-hover ${form.method === "postin" ? "bg-accent" : "bg-white"}`}>
              <h3 className="font-display font-bold uppercase tracking-wider mb-2">Post it in</h3>
              <p className="text-sm text-neutral-600">Anywhere in the UK. Free next-day return delivery once it's fixed — guaranteed.</p>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2" htmlFor="date">Preferred date (optional)</label>
              <input id="date" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid="booking-date-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="phone">Contact phone</label>
              <input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="07…" data-testid="booking-phone-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
          </div>
          {form.method === "dropoff" && (
            <div className="mt-6" data-testid="slot-picker">
              <label className="eyebrow block mb-2">Arrival slot (optional)</label>
              <div className="grid grid-cols-4 gap-2">
                {SLOTS.map((t) => {
                  const full = (availability?.slots?.[t] || 0) >= 3;
                  return (
                    <button key={t} onClick={() => set("preferred_time", form.preferred_time === t ? "" : t)} disabled={full}
                      data-testid={`slot-${t.replace(":", "")}`}
                      className={`border-2 border-black px-2 py-2 font-display text-sm font-bold ${full ? "bg-neutral-100 text-neutral-400 line-through cursor-not-allowed" : `shadow-hard-sm brutal-hover ${form.preferred_time === t ? "bg-accent" : "bg-white"}`}`}>
                      {full ? `${t} ✕` : t}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                {availability && availability.total >= 20
                  ? "That day is fully booked — pick another date."
                  : "Weekdays only — pick a date above and a time here so we're ready for you. Max 3 arrivals per slot."}
              </p>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div data-testid="booking-step-review">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-6">Check it over</h2>
          <dl className="border-2 border-black shadow-hard divide-y-2 divide-black mb-8">
            {[
              ["Device", `${form.brand} ${form.model} (${DEVICE_TYPES.find((d) => d.id === form.device_type)?.label || form.device_type})`],
              ["Problem", form.issue + (form.description ? ` — ${form.description}` : "")],
              ["Delivery", form.method === "dropoff" ? "Bring it to us" : "Post it in"],
              ["Arrival slot", form.method === "dropoff" ? ([form.preferred_date, form.preferred_time].filter(Boolean).join(" at ") || "Any time in opening hours") : "—"],
              ["Contact phone", form.phone],
            ].map(([k, v]) => (
              <div key={k} className="grid grid-cols-3 gap-4 px-5 py-3">
                <dt className="eyebrow pt-1">{k}</dt>
                <dd className="col-span-2 font-medium">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="flex justify-between mt-10">
        <button onClick={() => setStep((s) => s - 1)} disabled={step === 0} data-testid="booking-back-btn"
          className="inline-flex items-center gap-2 border-2 border-black bg-white px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard-sm brutal-hover disabled:opacity-30 disabled:pointer-events-none">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {step < 3 ? (
          <button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="booking-next-btn"
            className="inline-flex items-center gap-2 border-2 border-black bg-black text-white px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover disabled:opacity-30 disabled:pointer-events-none">
            Next <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={busy} data-testid="booking-submit-btn"
            className="inline-flex items-center gap-2 border-2 border-black bg-accent px-8 py-3 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover disabled:opacity-50">
            {busy ? "Booking…" : "Confirm booking"}
          </button>
        )}
      </div>
    </div>
  );
}
